import styled from '@emotion/styled';
import Link from 'next/link';
import { FC } from 'react';

import { FontFamily } from '@/enums/index';
import { NewsArticle } from '@/resources/NewsArticle';
import { BREAKPOINTS } from '@/styles/themes/default';
import { colors } from '@/styles/themes/foundations/colors';
import { IThemeProps } from '@/styles/themes/types';
import { lineClampStyles } from '@/styles/utils';

import { ClampedMarkdown } from '../ClampedMarkdown';

const ArticleContainer = styled.section<IThemeProps>`
  margin-top: 24px;
  background-color: white;
  cursor: pointer;

  &:hover h4 {
    font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGroteskUnderline]};
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    position: relative;
    margin-top: 40px;
  }
`;

const ArticleLink = styled(Link)<IThemeProps>`
  text-decoration: none;
  display: flex;
  flex-direction: column;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    flex-direction: row;
    min-height: 400px;
  }
`;

const ArticleTitle = styled.h3<IThemeProps>`
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  color: black;
  font-size: 32px;
  line-height: 40px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    font-size: 48px;
    line-height: 56px;
    min-height: 100px;
  }
`;

const ArticleImageContainer = styled.div<IThemeProps>`
  position: relative;
  cursor: pointer;
  flex-shrink: 1;
  order: 1;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    height: 385px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    order: 2;
    width: 690px;
    height: 480px;
  }
`;

const ArticleImage = styled.img<IThemeProps>`
  width: 100%;
  /* gray.50 (#F3F5F5) shows through transparent artwork */
  background-color: ${colors.gray[50]};
  height: 100%;
  object-fit: cover;
  display: block;
  max-height: 300px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    max-height: 385px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    max-width: 690px;
    max-height: 480px;
  }
`;

const ArticleDetailsContainer = styled.div<IThemeProps>`
  padding: 24px;
  order: 2;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding: 32px 48px 40px 48px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    padding: 56px 40px 56px 56px;
    flex: 1;
    max-height: 480px;
    min-width: 380px;
    order: 1;
  }
`;

const ArticleInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ArticleInfo = styled.p<IThemeProps>`
  font-size: 16px;
  line-height: 24px;
  margin: 0 0 14px;
  color: ${({ theme }) => theme.legacy.colors.lightText};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSerif]};
  font-weight: normal;
`;

const ArticleAuthor = styled.span<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.brand.primary};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  font-weight: bold;
  margin-right: 16px;
`;

const Description = styled.div<IThemeProps>`
  margin: 24px 0 0;
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSerif]};
  min-height: 100px;
  color: ${({ theme }) => theme.legacy.colors.lightText};
`;

const StyledMarkdown = styled(ClampedMarkdown)`
  height: 120px;
  ${lineClampStyles(4)}
  & > * {
    display: none;
  }

  /* we are using ClampedMarkdown component which adds class to first node */
  /* because first-child can't be used, and first-of-type is not the solution */
  & > .first-markdown-element {
    margin: 0;
    display: initial;
  }
`;

interface IRecommendedArticleProps {
  article: NewsArticle;
}

export const FeaturedArticle: FC<IRecommendedArticleProps> = ({ article }) => {
  return (
    <ArticleContainer>
      <ArticleLink href={`/news/${article.linkSlug}`} target="_blank" rel="noopener noreferrer">
        <ArticleDetailsContainer>
          <ArticleInfoContainer>
            <ArticleInfo>
              <ArticleAuthor>{article.author}</ArticleAuthor>&nbsp;
              {article.readTime && `${article.readTime} min read`}
            </ArticleInfo>
          </ArticleInfoContainer>
          <ArticleTitle css={lineClampStyles(3)}>{article.title}</ArticleTitle>
          {article.summary && (
            <Description>
              <StyledMarkdown source={article.summary} />
            </Description>
          )}
        </ArticleDetailsContainer>
        <ArticleImageContainer>
          <ArticleImage src={article.largeFeaturedImageUrl} />
        </ArticleImageContainer>
      </ArticleLink>
    </ArticleContainer>
  );
};
